import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
// your thunks
import { updateYadsTenantDetails, patchYadsTenantReset } from "../../../Redux/Dashboard/External/Yads/YadsEditScheduleActions";
import { rescheduleYadsScenario, rescheduleYadsReset } from "../../../Redux/Dashboard/External/Yads/YadsEditScheduleActions";
import { getYadsPollingStatus } from "../../../Redux/Dashboard/External/Yads/GetPollingStatusAction";
import { getTenantScenarios } from '../../../Redux/Dashboard/External/Yads/GetTenantScenGroupAction';

import Swal from "sweetalert2";
import "../../../Styles/StickyStatusBar.css";
import "../../../Styles/ScheduleIngestionModal.css";


import LoadingScreen from '../../../ReusableBlocks/LoadingScreen';



/** ---------- id normalizer ---------- */
const withIds = (scenarioGroups = []) =>
    (scenarioGroups || []).map((g) => ({
        ...g,
        scenarios: (g.scenarios || []).map((s) => ({
            ...s,
            id: s.id || s.title,
            runMode: s.runMode ?? null,
            count: s.count ?? 1,
            durationHours: s.durationHours ?? 1,
            selected: false,
        })),
    }));

/** ---------- core component ---------- */
const Yads_Ingestion_Scheduled_Edit = ({ isOpen, onClose, record, onSubmit }) => {




    const dispatch = useDispatch();




    // pull shared data
    const { tenantOptions = [], scenarioGroups: rawGroups = [] } =
        useSelector((s) => s.YadsTenantScenario || {});
    const scenarioGroups = useMemo(() => withIds(rawGroups), [rawGroups]);


    const {
        loading: patchLoading = false,
        result: patchResult = null,
        error: patchError = "",
    } = useSelector((s) => s.YadsPatchTenant || {});

    const {
        loading: resLoading = false,
        result: resResult = null,
        error: resError = "",
    } = useSelector((s) => s.YadsReschedule || {});



    /** ---- date/time helpers & state (matching ScheduleIngestionModal) ---- */
    const pad2 = (n) => String(n).padStart(2, "0");
    const to24h = (h12, ampm) => {
        let h = parseInt(h12, 10);
        if (ampm === "PM" && h < 12) h += 12;
        if (ampm === "AM" && h === 12) h = 0;
        return h;
    };
    const getNext5MinSlot = (d = new Date()) => {
        const copy = new Date(d);
        const m = copy.getMinutes();
        const rounded = Math.ceil(m / 5) * 5;
        if (rounded === 60) {
            copy.setHours(copy.getHours() + 1, 0, 0, 0);
        } else {
            copy.setMinutes(rounded, 0, 0);
        }
        return copy;
    };
    const todayLocalDateStr = (() => {
        const d = new Date();
        d.setHours(0, 0, 0, 0);
        return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
    })();

    const HOURS_12 = Array.from({ length: 12 }, (_, i) => pad2(i + 1));
    const MINUTES = Array.from({ length: 12 }, (_, i) => pad2(i * 5));

    const [dateStr, setDateStr] = useState("");
    const [hourStr, setHourStr] = useState("");     // "01".."12"
    const [minuteStr, setMinuteStr] = useState(""); // "00","05",...,"55"
    const [ampm, setAmpm] = useState("AM");
    const [minTodaySlot, setMinTodaySlot] = useState(getNext5MinSlot());



    // RTIC: null = untouched (don’t send); true/false = user explicitly set
    const [resetRticCluster, setResetRticCluster] = useState(null);
    const [rticTouched, setRticTouched] = useState(false);




    /** ---------- modal-local state ---------- */
    const [date, setDate] = useState("");
    const [time, setTime] = useState("");

    // selection model (same structure/behavior as main page)
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [selectedGroupScenarios, setSelectedGroupScenarios] = useState([]);
    const [groupCheckboxes, setGroupCheckboxes] = useState({});
    const [selectedItems, setSelectedItems] = useState([]); // right panel tags
    const [scenarioModes, setScenarioModes] = useState({}); // 'Default' | 'Advanced' | 'Reset'
    const selectAllRef = React.useRef(null);



    const tenantUrl = record?.tenantUrl;
    const tenant = tenantOptions.find(t => t.tenantUrl === tenantUrl);


    // Editable tenant form (prefilled from tenantOptions by tenantUrl in record)
    const [tenantForm, setTenantForm] = useState({
        tenantUrl: "",
        itsmUrl: "",
        key: "",
        secretKey: "",
        username: "",
        password: "",
    });

    // snapshot of the original values when modal opens
    const [initialSnap, setInitialSnap] = useState({
        tenant: null,
        dateStr: "",
        hourStr: "",
        minuteStr: "",
        ampm: "AM",
        items: [],
    });

    // ----- compare helpers -----
    const normTenant = (t = {}) => ({
        tenantUrl: t.tenantUrl?.trim() || "",
        itsmUrl: t.itsmUrl?.trim() || "",
        key: t.key?.trim() || "",
        secretKey: t.secretKey?.trim() || "",
        username: t.username?.trim() || "",
        password: t.password?.trim() || "",
    });
    const sameTenant = (a, b) => {
        const A = normTenant(a), B = normTenant(b);
        return (
            A.tenantUrl === B.tenantUrl &&
            A.itsmUrl === B.itsmUrl &&
            A.key === B.key &&
            A.secretKey === B.secretKey &&
            A.username === B.username &&
            A.password === B.password
        );
    };

    const sameSchedule = (a, b) =>
        a.dateStr === b.dateStr &&
        a.hourStr === b.hourStr &&
        a.minuteStr === b.minuteStr &&
        a.ampm === b.ampm;

    // normalize selected items for equality (ignore order)
    const normalizeItems = (items = []) => {
        // try to preserve “logical selection” (scenario id + parsed count, and group/reset tags)
        const toKey = (it) => {
            const id = it.id || "";
            const group = it.group || "";
            let count = 1;
            // label like "Scenario-Title - 3"
            if (typeof it.label === "string") {
                const parts = it.label.split(" - ");
                const c = parseInt(parts[parts.length - 1], 10);
                if (!isNaN(c)) count = c;
            }
            // encode: type|group|id|count
            const type =
                id.startsWith("group-") ? "group" :
                    id.startsWith("reset-") || it.type === "reset" ? "reset" :
                        "scenario";
            return `${type}|${group}|${id}|${count}`;
        };

        return (items || []).map(toKey).sort();
    };
    const sameItems = (a, b) => {
        const A = normalizeItems(a);
        const B = normalizeItems(b);
        if (A.length !== B.length) return false;
        for (let i = 0; i < A.length; i++) if (A[i] !== B[i]) return false;
        return true;
    };






    /** ---------- helpers from main page logic ---------- */
    // put near your other helpers
    const isInitTitle = (title = "") => /-Init$/i.test(title.trim());
    const cleanupReFor = (groupKey) =>
        new RegExp(`^${groupKey.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}(?:[\\s-]*)Cleanup(?:$|[\\s-])`, "i");
    const titleToGroup = (title, groups) =>
        (groups || []).find((g) => title?.startsWith(g.title))?.title || null;
    const isInitScenario = (s) => isInitTitle(s?.title);

    const escapeRegExp = (s = "") => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const cleanupMatcher = (groupKey) =>
        new RegExp(`^${escapeRegExp(groupKey)}(?:[\\s-]*)Cleanup(?:$|[\\s-])`, "i");

    const findGroupCleanups = (groupKey) => {
        const group = (scenarioGroups || []).find((g) => g?.title === groupKey);
        if (!group) return [];
        const re = cleanupMatcher(groupKey);
        return (group.scenarios || [])
            .map((s) => s.title?.trim())
            .filter(Boolean)
            .filter((title) => re.test(title));
    };

    const deriveGroupFromTitle = (title = "") => {
        // safest: find the group whose title is a prefix of the scenario title
        // e.g. "Apex-Mobile-HighMetric" starts with "Apex-Mobile"
        const groups = scenarioGroups || [];
        return groups.find((g) => title.startsWith(g.title))?.title || null;
    };

    const getCurrentMode = () =>
        selectedGroup ? scenarioModes[selectedGroup] || "Default" : "Default";

    const getAdvancedSelectionState = () => {
        if (!selectedGroupScenarios.length) return "none";
        const selectedCount = selectedGroupScenarios.filter((s) => s.selected).length;
        if (selectedCount === 0) return "none";
        if (selectedCount === selectedGroupScenarios.length) return "all";
        return "some";
    };



    const getScenarioRunMode = (scenario) => {
        const mode = String(scenario?.runMode ?? "").toLowerCase().trim();

        if (mode === "duration") return "duration";
        if (mode === "count") return "count";

        if (scenario?.durationHours != null) return "duration";

        return "count";
    };

    const isDurationScenario = (scenario) => getScenarioRunMode(scenario) === "duration";

    const getScenarioValue = (scenario) => {
        if (isDurationScenario(scenario)) {
            return parseInt(scenario?.durationHours, 10) || 1;
        }
        return parseInt(scenario?.count, 10) || 1;
    };

    const buildScenarioLabel = (scenario) => {
        const value = getScenarioValue(scenario);
        return isDurationScenario(scenario)
            ? `${scenario.title} - ${value} hour${value > 1 ? "s" : ""}`
            : `${scenario.title} - ${value}`;
    };


    /** ---------- left pane: click a group ---------- */
    // ---------- left pane: click a group ----------
    const restoreGroupScenarios = (group) => {
        const groupKey = group.title;

        // HARD RESET like Yads_Ingestion (enforce one group at a time)
        setSelectedGroupScenarios([]);
        setSelectedItems([]);
        // setGroupScenarioSelectionMap({}); // ⬅️ remove this line — not defined in this component
        setGroupCheckboxes({});
        setScenarioModes({});

        setSelectedGroup(groupKey);

        // Honor saved selection for this group if present; else Default
        const saved = preselectByGroup[groupKey];

        if (!saved) {
            // Default: all selected + one group tag
            const groupScenarios = (group.scenarios || []).map((s) => ({
                ...s,
                selected: true,
                count: s.count || 1,
            }));
            setSelectedGroupScenarios(groupScenarios);
            setSelectedItems([{ id: `group-${groupKey}`, group: groupKey, label: groupKey }]);
            setGroupCheckboxes({ [groupKey]: "checked" });
            setScenarioModes({ [groupKey]: "Default" });
            return;
        }

        const startMode = saved.mode || "Advanced";
        setScenarioModes({ [groupKey]: startMode });

        if (startMode === "Reset") {
            const cleanupTags = saved.items.map((it) => ({
                id: it.id,
                type: "scenario",
                group: groupKey,
                label: `${it.title} - ${it.count || 1}`,
            }));
            setSelectedItems(
                cleanupTags.length
                    ? cleanupTags
                    : [{ id: `reset-${groupKey}`, type: "reset", group: groupKey, label: `${groupKey} Cleanup` }]
            );
            setGroupCheckboxes({ [groupKey]: "none" });
            setSelectedGroupScenarios([]); // empty middle list in Reset
            return;
        }

        if (startMode === "Default") {
            const groupScenarios = (group.scenarios || []).map((s) => ({
                ...s,
                selected: true,
                count: s.count || 1,
                durationHours: s.durationHours || 1,
            }));
            setSelectedGroupScenarios(groupScenarios);
            setSelectedItems([{ id: `group-${groupKey}`, group: groupKey, label: groupKey }]);
            setGroupCheckboxes({ [groupKey]: "checked" });
            return;
        }

        // Advanced: mark saved items as selected with counts
        const byId = Object.fromEntries(saved.items.map((it) => [it.id, it.count || 1]));
        const updated = (group.scenarios || []).map((s) => {
            const savedValue = byId[s.id] ?? 1;

            return {
                ...s,
                selected: Object.prototype.hasOwnProperty.call(byId, s.id),
                count: isDurationScenario(s) ? (s.count || 1) : savedValue,
                durationHours: isDurationScenario(s) ? savedValue : (s.durationHours || 1),
            };
        });
        setSelectedGroupScenarios(updated);

        // Right pane: only this group's selected scenarios
        const tags = updated
            .filter((s) => s.selected)
            .map((s) => ({ id: s.id, group: groupKey, label: buildScenarioLabel(s) }));
        setSelectedItems(tags);

        const total = updated.length;
        const selectedCount = updated.filter((s) => s.selected).length;
        setGroupCheckboxes({
            [groupKey]: selectedCount === 0 ? "none" : selectedCount === total ? "checked" : "partial",
        });
    };



    /** ---------- middle pane: mode changes ---------- */
    const handleScenarioModeChange = (mode, customGroupKey) => {
        const groupKey = customGroupKey || selectedGroup;
        if (!groupKey) return;

        const prevMode = scenarioModes[groupKey] || "Default";

        // ✅ If we are leaving Reset, RTIC must be cleared (no hidden carryover)
        if (prevMode === "Reset" && mode !== "Reset") {
            setResetRticCluster(null);
            setRticTouched(false);
        }

        setScenarioModes((prev) => ({ ...prev, [groupKey]: mode }));
        const groupObj = scenarioGroups.find((g) => g.title === groupKey);
        if (!groupObj) return;

        if (mode === "Default") {
            // All scenarios selected; preserve any existing counts if we had them in the middle pane.
            const groupScenarios = (groupObj.scenarios || []).map((s) => {
                const existing = selectedGroupScenarios.find((x) => x.id === s.id);

                return {
                    ...s,
                    selected: true,
                    count: existing?.count || s.count || 1,
                    durationHours: existing?.durationHours || s.durationHours || 1,
                };
            });
            setSelectedGroupScenarios(groupScenarios);

            // Right pane: single GROUP tag (overwrite this group's tags).
            setSelectedItems([
                { id: `group-${groupKey}`, group: groupKey, label: groupKey },
            ]);

            setGroupCheckboxes((prev) => ({ ...prev, [groupKey]: "checked" }));
            return;
        }

        if (mode === "Advanced") {
            // Start with everything unselected.
            const updatedScenarios = (groupObj.scenarios || []).map((s) => ({
                ...s,
                selected: false,
                count: s.count || 1,
                durationHours: s.durationHours || 1,
            }));

            // If right pane already had scenario tags for this group, hydrate from there.
            const tagsForGroup = selectedItems.filter(
                (t) => t.group === groupKey && !t.id.startsWith("group-")
            );
            if (tagsForGroup.length) {
                const mapCounts = Object.fromEntries(
                    tagsForGroup.map((t) => {
                        const parts = String(t.label).split(" - ");
                        const parsed = parseInt(parts[parts.length - 1], 10) || 1;
                        return [t.id, parsed];
                    })
                );

                updatedScenarios.forEach((s) => {
                    if (mapCounts[s.id]) {
                        s.selected = true;

                        if (isDurationScenario(s)) {
                            s.durationHours = mapCounts[s.id];
                        } else {
                            s.count = mapCounts[s.id];
                        }
                    }
                });
            }

            setSelectedGroupScenarios(updatedScenarios);

            // Right pane: ONLY this group's selected scenarios (overwrite).
            const scenarioTags = updatedScenarios
                .filter((s) => s.selected)
                .map((s) => ({
                    id: s.id,
                    group: groupKey,
                    label: buildScenarioLabel(s),
                }));
            setSelectedItems(scenarioTags);

            // Checkbox state for this group.
            const total = updatedScenarios.length;
            const selectedCount = updatedScenarios.filter((s) => s.selected).length;
            setGroupCheckboxes((prev) => ({
                ...prev,
                [groupKey]:
                    selectedCount === 0
                        ? "none"
                        : selectedCount === total
                            ? "checked"
                            : "partial",
            }));
            return;
        }

        if (mode === "Reset") {
            // Middle list is empty in Reset.
            setSelectedGroupScenarios([]);

            // Build cleanup tags for this group.
            const cleanupTitles = findGroupCleanups(groupKey);
            const cleanupTags = cleanupTitles.map((title) => {
                const scenario = (groupObj.scenarios || []).find((s) => s.title === title);
                return {
                    id: scenario?.id || title,
                    type: "scenario",
                    group: groupKey,
                    label: `${title} - 1`,
                };
            });

            // Right pane: cleanup tags (or a single fallback reset tag) — overwrite.
            setSelectedItems(
                cleanupTags.length
                    ? cleanupTags
                    : [
                        {
                            id: `reset-${groupKey}`,
                            type: "reset",
                            group: groupKey,
                            label: `${groupKey} Cleanup`,
                        },
                    ]
            );

            setGroupCheckboxes((prev) => ({ ...prev, [groupKey]: "none" }));
            return;
        }
    };


    /** ---------- middle pane: per-scenario interactions ---------- */
    const handleScenarioToggle = (idx) => {
        if (getCurrentMode() === "Reset") return;
        const updated = [...selectedGroupScenarios];
        const scenario = updated[idx];
        scenario.selected = !scenario.selected;
        setSelectedGroupScenarios(updated);

        // update right panel in Advanced mode (one-group-at-a-time)
        if (getCurrentMode() === "Advanced") {
            const scenarioTags = updated
                .filter((s) => s.selected)
                .map((s) => ({ id: s.id, group: selectedGroup, label: buildScenarioLabel(s) }));
            // keep non-this-group tags
            setSelectedItems((prev) => [
                ...prev.filter((it) => it.group !== selectedGroup),
                ...scenarioTags,
            ]);
        }

        // checkbox UI
        const total = updated.length;
        const selectedCount = updated.filter((s) => s.selected).length;
        setGroupCheckboxes((prev) => ({
            ...prev,
            [selectedGroup]:
                selectedCount === 0 ? "none" : selectedCount === total ? "checked" : "partial",
        }));
    };


    const deriveGroupFromItem = (item) => {
        if (item?.group) return item.group;
        if (typeof item?.id === "string") {
            const m = item.id.replace(/(?:\s|-)*Cleanup(?:-.*)?$/i, "").trim();
            if (m) return m;
        }
        if (typeof item?.label === "string") {
            const m = item.label.replace(/(?:\s|-)*Cleanup(?:-.*)?$/i, "").trim();
            if (m) return m;
        }
        return null;
    };

    /** Convert right-pane selectedItems → scenarios payload */
    const selectedItemsToScenariosPayload = (selectedItems, scenarioGroups) => {
        const scenarioSet = new Set();       // de-dup scenarios
        const scenariosPayload = [];         // ordered result

        const _cleanupMatcher = (groupKey) =>
            new RegExp(`^${groupKey.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}(?:[\\s-]*)Cleanup(?:$|[\\s-])`, "i");

        const _findGroupCleanups = (groupKey) => {
            const group = (scenarioGroups || []).find((g) => g?.title === groupKey);
            if (!group) return [];
            const re = _cleanupMatcher(groupKey);
            return (group.scenarios || [])
                .map((s) => s.title?.trim())
                .filter(Boolean)
                .filter((title) => re.test(title));
        };

        selectedItems.forEach((item) => {
            // 1) Reset/Cleanup → expand to all cleanup variants for the group
            if (item?.type === "reset" || (typeof item?.id === "string" && item.id.startsWith("reset-"))) {
                const groupKey = deriveGroupFromItem(item);
                if (groupKey) {
                    let titles = _findGroupCleanups(groupKey);

                    // safety fallback: scan all titles if group scens not loaded
                    if (!titles.length) {
                        const allTitles = (Array.isArray(scenarioGroups) ? scenarioGroups : [])
                            .flatMap((g) => (g.scenarios || []).map((s) => s.title?.trim()))
                            .filter(Boolean);
                        const re = _cleanupMatcher(groupKey);
                        titles = allTitles.filter((t) => re.test(t));
                    }

                    titles.forEach((title) => {
                        if (!scenarioSet.has(title)) {
                            scenarioSet.add(title);
                            scenariosPayload.push({ type: "scenario", title, count: 1 });
                        }
                    });
                }
                return;
            }

            // 2) Group tag → single group entry
            if (item.id.startsWith("group-")) {
                scenariosPayload.push({
                    type: "group",
                    title: item.label, // label is the group title
                    count: 1,
                });
                return;
            }

            // 3) Scenario tag → map id → catalog title, keep count from label "Title - N"
            if (!scenarioSet.has(item.id)) {
                const scenario = (scenarioGroups || [])
                    .flatMap((g) => g.scenarios || [])
                    .find((s) => s.id === item.id || s.title === item.id);

                if (scenario) {
                    scenarioSet.add(scenario.id);
                    const parts = String(item.label).split(" - ");
                    const parsedValue = parseInt(parts[parts.length - 1], 10) || 1;

                    const payloadScenario = {
                        type: "scenario",
                        title: scenario.title,
                    };

                    if (isDurationScenario(scenario)) {
                        payloadScenario.durationHours = parsedValue;
                    } else {
                        payloadScenario.count = parsedValue;
                    }

                    scenariosPayload.push(payloadScenario);
                }
            }
        });

        return scenariosPayload;
    };

    const handleSelectAllScenarios = () => {
        const allSelected = selectedGroupScenarios.every((s) => s.selected);
        const nextSelected = !allSelected;
        const updated = selectedGroupScenarios.map((s) => ({ ...s, selected: nextSelected }));
        setSelectedGroupScenarios(updated);

        setGroupCheckboxes((prev) => ({ ...prev, [selectedGroup]: nextSelected ? "checked" : "none" }));

        if (getCurrentMode() === "Advanced") {
            const tags = nextSelected
                ? updated.map((s) => ({
                    id: s.id,
                    group: selectedGroup,
                    label: buildScenarioLabel(s),
                }))
                : [];
            setSelectedItems((prev) => [
                ...prev.filter((it) => it.group !== selectedGroup),
                ...tags,
            ]);
        }
    };

    const updateScenarioCount = (idx, newVal) => {
        const updated = [...selectedGroupScenarios];
        if (isInitScenario(updated[idx])) return;

        const current = updated[idx];

        if (isDurationScenario(current)) {
            updated[idx].durationHours = newVal;
        } else {
            updated[idx].count = newVal;
        }

        setSelectedGroupScenarios(updated);
    };

    const sanitizeScenarioCount = (idx, raw) => {
        let value = parseInt(raw, 10);
        if (isNaN(value) || value <= 0) value = 1;

        const updated = [...selectedGroupScenarios];
        if (isInitScenario(updated[idx])) return;

        const current = updated[idx];

        if (isDurationScenario(current)) {
            updated[idx].durationHours = value;
        } else {
            updated[idx].count = value;
        }

        setSelectedGroupScenarios(updated);

        const s = updated[idx];
        setSelectedItems((prev) =>
            prev.map((it) =>
                it.id === s.id ? { ...it, group: selectedGroup, label: buildScenarioLabel(s) } : it
            )
        );
    };

    /** ---------- right pane ---------- */
    const handleRemoveSelectedItem = (idToRemove) => {
        const isGroupTag = idToRemove.startsWith("group-");
        const groupKey = isGroupTag ? idToRemove.replace("group-", "") : null;

        if (isGroupTag) {
            setSelectedItems((prev) => prev.filter((it) => it.group !== groupKey));
            if (selectedGroup === groupKey) {
                const updated = selectedGroupScenarios.map((s) => ({ ...s, selected: false }));
                setSelectedGroupScenarios(updated);
                setGroupCheckboxes((prev) => ({ ...prev, [groupKey]: "none" }));
            }
            return;
        }

        // remove scenario tag
        setSelectedItems((prev) => prev.filter((it) => it.id !== idToRemove));

        // reflect in middle pane if same group open
        setSelectedGroupScenarios((prev) => {
            const updated = prev.map((s) => (s.id === idToRemove ? { ...s, selected: false } : s));
            const total = updated.length;
            const selectedCount = updated.filter((s) => s.selected).length;
            setGroupCheckboxes((p) => ({
                ...p,
                [selectedGroup]:
                    selectedCount === 0 ? "none" : selectedCount === total ? "checked" : "partial",
            }));
            return updated;
        });
    };

    const handleDragEnd = (result) => {
        if (!result.destination) return;
        const items = Array.from(selectedItems);
        const [reordered] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reordered);
        setSelectedItems(items);
    };


    // map of group -> { mode: 'Default' | 'Advanced' | 'Reset', items: [{title, id, count}] }
    const preselectByGroup = React.useMemo(() => {
        if (!record || !Array.isArray(record.scenarioProgress)) return {};
        const map = {};

        record.scenarioProgress.forEach((sp) => {
            const title = sp.title?.trim();
            if (!title) return;

            const groupKey = titleToGroup(title, scenarioGroups) || "Unknown";
            if (!map[groupKey]) {
                map[groupKey] = { mode: "Advanced", items: [] };
            }

            const catalogScenario =
                (scenarioGroups || [])
                    .flatMap((g) => g.scenarios || [])
                    .find((s) => s.title === title);

            const id = catalogScenario?.id || title;

            const value =
                catalogScenario && isDurationScenario(catalogScenario)
                    ? Number(sp.durationHours ?? 1)
                    : Number(sp.count ?? 1);

            map[groupKey].items.push({
                title,
                id,
                count: value, // keep field name as count for existing downstream logic
            });
        });

        // If a group's saved items are all cleanup variants → start in Reset
        Object.keys(map).forEach((gk) => {
            const re = cleanupReFor(gk);
            const allCleanup =
                map[gk].items.length > 0 &&
                map[gk].items.every((it) => re.test(it.title));
            if (allCleanup) map[gk].mode = "Reset";
        });

        return map;
    }, [record, scenarioGroups]);

    // put near other helpers
    const initialTenantForm = {
        tenantUrl: "",
        itsmUrl: "",
        key: "",
        secretKey: "",
        username: "",
        password: "",
    };

    const resetModalState = () => {
        // left/middle/right panes
        setSelectedGroup(null);
        setSelectedGroupScenarios([]);
        setSelectedItems([]);
        setGroupCheckboxes({});
        setScenarioModes({});

        // schedule picker (12h UI)
        setDateStr("");
        setHourStr("");
        setMinuteStr("");
        setAmpm("AM");
        setMinTodaySlot(getNext5MinSlot());

        // (legacy date/time inputs, if still used anywhere)
        setDate("");
        setTime("");

        // tenant form
        setTenantForm(initialTenantForm);
    };

    // Build preselect map + open the first group
    // Build preselect map + open the first group
    const hydrateFromRecord = () => {
        if (!record) return;

        // tenant (from store if available)
        const t = (tenantOptions || []).find(x => x.tenantUrl === record.tenantUrl);
        setTenantForm({
            tenantUrl: t?.tenantUrl || record.tenantUrl || "",
            itsmUrl: t?.itsmUrl || "",
            key: t?.key || "",
            secretKey: t?.secretKey || "",
            username: t?.username || "",
            password: t?.password || "",
        });

        // schedule (from record.scheduledDate) — use locals for snapshot
        const d = new Date(record.scheduledDate);
        let ds = "", hs = "", ms = "", ap = "AM";
        if (!Number.isNaN(d)) {
            ds = `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
            const h24 = d.getHours();
            const mm = d.getMinutes();
            ap = h24 >= 12 ? "PM" : "AM";
            let h12 = h24 % 12; if (h12 === 0) h12 = 12;
            hs = pad2(h12);
            ms = pad2(mm - (mm % 5)); // snap to 5-min

            setDateStr(ds);
            setHourStr(hs);
            setMinuteStr(ms);
            setAmpm(ap);
        }

        // selected-tags (just to show on right initially)
        const tags = (record.scenarioProgress || []).map(sp => {
            const groupKey = deriveGroupFromTitle(sp.title) || "Unknown";
            const id =
                (scenarioGroups.flatMap(g => g.scenarios)
                    .find(s => s.title === sp.title)?.id) || sp.title;
            const catalogScenario =
                scenarioGroups.flatMap(g => g.scenarios).find(s => s.title === sp.title);

            const value = catalogScenario && isDurationScenario(catalogScenario)
                ? Number(sp.durationHours ?? 1)
                : Number(sp.count ?? 1);

            return {
                id,
                group: groupKey,
                label: catalogScenario && isDurationScenario(catalogScenario)
                    ? `${sp.title} - ${value} hour${value > 1 ? "s" : ""}`
                    : `${sp.title} - ${value}`,
            };
        });
        setSelectedItems(tags);

        // open the first saved group with proper mode/rows
        const firstTitle = record?.scenarioProgress?.[0]?.title;
        const firstGroup = firstTitle ? deriveGroupFromTitle(firstTitle) : (scenarioGroups[0]?.title || null);
        if (firstGroup) {
            const groupObj = scenarioGroups.find(g => g.title === firstGroup);
            if (groupObj) restoreGroupScenarios(groupObj);
        }

        // ---- RTIC (backend now returns resetRticCluster true/false) ----
        const rticFromApi =
            typeof record?.resetRticCluster === "boolean"
                ? record.resetRticCluster
                : null;
        setResetRticCluster(rticFromApi);
        setRticTouched(false);

        // 🔹 TAKE INITIAL SNAPSHOT HERE (now t & tags are in scope)
        setInitialSnap({
            tenant: normTenant({
                tenantUrl: (t?.tenantUrl || record.tenantUrl || ""),
                itsmUrl: (t?.itsmUrl || ""),
                key: (t?.key || ""),
                secretKey: (t?.secretKey || ""),
                username: (t?.username || ""),
                password: (t?.password || ""),
            }),
            dateStr: ds,
            hourStr: hs,
            minuteStr: ms,
            ampm: ap,
            items: tags,
        });
    };


    // ⬇️ RESET on close and HYDRATE on open
    useEffect(() => {
        if (!isOpen) {
            resetModalState();
            return;
        }
        // wait until scenarioGroups are ready, then hydrate
        if ((scenarioGroups || []).length) {
            resetModalState();       // ensure clean slate each open
            hydrateFromRecord();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, record, scenarioGroups]);





    // Safely pick a message from various API shapes (success or error)
    const pickApiMessage = (obj) => {
        if (!obj) return "";

        // If thunk stored a raw string
        if (typeof obj === "string") return obj;

        // Common places servers put messages
        return (
            obj.message ||
            obj.error ||
            obj.success ||
            obj.error
        );
    };



    // Tenant PATCH result
    useEffect(() => {
        if (patchLoading) return;

        if (patchResult) {
            Swal.fire({
                icon: "success",
                title: "Updated",
                text: pickApiMessage(patchResult) || "Tenant updated.",
                confirmButtonColor: "#05264e",
            });
            dispatch(patchYadsTenantReset());
            dispatch(getYadsPollingStatus());
            onClose?.(); // close the modal on success
        } else if (patchError) {
            Swal.fire({
                icon: "error",
                title: "Update failed",
                text: pickApiMessage(patchError) || "Something went wrong.",
                confirmButtonColor: "#05264e",
            });
            dispatch(patchYadsTenantReset());
        }
    }, [patchLoading, patchResult, patchError, dispatch, onClose]);

    // Reschedule POST result
    useEffect(() => {
        if (resLoading) return;

        if (resResult) {
            Swal.fire({
                icon: "success",
                title: "Rescheduled",
                text: pickApiMessage(resResult) || "Schedule updated.",
                confirmButtonColor: "#05264e",
            });
            dispatch(rescheduleYadsReset());
            dispatch(getYadsPollingStatus());
            onClose?.(); // close the modal on success
        } else if (resError) {
            Swal.fire({
                icon: "error",
                title: "Reschedule failed",
                text: pickApiMessage(resError) || "Something went wrong.",
                confirmButtonColor: "#05264e",
            });
            dispatch(rescheduleYadsReset());
        }
    }, [resLoading, resResult, resError, dispatch, onClose]);




    // keep the select-all checkbox indeterminate state
    useEffect(() => {
        if (selectAllRef.current) {
            selectAllRef.current.indeterminate = getAdvancedSelectionState() === "some";
        }
    }, [selectedGroup, selectedGroupScenarios, scenarioModes]);


    const handleSave = () => {
        const newTenant = normTenant(tenantForm);
        const newScheduleParts = { dateStr, hourStr, minuteStr, ampm };

        const changedTenant = !sameTenant(initialSnap.tenant, newTenant);
        const changedSchedule = !sameSchedule(initialSnap, newScheduleParts);
        const changedScenarios = !sameItems(initialSnap.items, selectedItems);

        // ---- Build scenarios ALWAYS (needed for POST) ----
        const scenariosPayload = selectedItemsToScenariosPayload(selectedItems, scenarioGroups);
        if (!Array.isArray(scenariosPayload) || scenariosPayload.length === 0) {
            Swal.fire("No scenarios", "Please select at least one scenario or group.", "warning");
            return;
        }

        // ---- Build scheduled time ALWAYS (needed for POST) ----
        // Prefer picker values if present; else fall back to record.scheduledDate
        let scheduledDateISO = null;
        if (dateStr && hourStr && minuteStr && ampm) {
            const h24 = to24h(hourStr, ampm);
            const [y, m, d] = dateStr.split("-").map(Number);
            scheduledDateISO = new Date(y, m - 1, d, h24, parseInt(minuteStr, 10), 0, 0).toISOString();
        } else if (record?.scheduledDate) {
            scheduledDateISO = new Date(record.scheduledDate).toISOString();
        }

        if (!scheduledDateISO) {
            Swal.fire("Missing schedule", "Please select date and time.", "warning");
            return;
        }

        // Optionally enforce future time (your call)
        if (new Date(scheduledDateISO) < new Date()) {
            Swal.fire("Invalid time", "Please choose a future date/time.", "warning");
            return;
        }

        // ---- Tenant validation only if tenant actually changed ----
        if (changedTenant) {
            const required = ["tenantUrl", "key", "secretKey", "username", "password"];
            const missing = required.filter((k) => !newTenant[k]);
            if (missing.length) {
                Swal.fire("Incomplete tenant details", `Please fill: ${missing.join(", ")}`, "warning");
                return;
            }
        }

        // ---- Payloads ----
        const tenantPayload = {
            tenantUrl: newTenant.tenantUrl,
            itsmUrl: newTenant.itsmUrl,
            key: newTenant.key,
            secretKey: newTenant.secretKey,
            username: newTenant.username,
            password: newTenant.password,
        };

        // For POST we must send BOTH scenarios and scheduledTime regardless of which changed
        const postPayload = {
            ...tenantPayload,                 // backend examples include creds on POST
            scenarios: scenariosPayload,
            scheduledTime: scheduledDateISO,
        };

        const isResetMode = getCurrentMode() === "Reset";

        // ✅ RTIC belongs ONLY to Reset mode
        if (isResetMode && rticTouched && resetRticCluster !== null) {
            postPayload.resetRticCluster = !!resetRticCluster;
        }


        // ---- Decide call ----
        if (changedTenant && !((changedSchedule && !changedScenarios) || rticTouched)) {
            // Case 1: PATCH tenant only
            dispatch(updateYadsTenantDetails(record.taskId, tenantPayload));
            dispatch(getTenantScenarios());
        } else if (changedSchedule || changedScenarios || rticTouched) {
            // Case 2: POST — ALWAYS send both scenarios & time
            dispatch(rescheduleYadsScenario(record.taskId, postPayload));
        } else {
            Swal.fire("No changes", "Nothing changed from the original values.", "question");
        }
    };




    /** ---------- close on ESC ---------- */
    const onKey = useCallback(
        (e) => {
            if (e.key === "Escape" && isOpen) onClose?.();
        },
        [isOpen, onClose]
    );
    useEffect(() => {
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [onKey]);




    const isTodaySelected = dateStr === todayLocalDateStr;

    useEffect(() => {
        if (!dateStr) return;
        if (!isTodaySelected) return;

        const nowSlot = getNext5MinSlot();
        setMinTodaySlot(nowSlot);

        if (!hourStr || !minuteStr) {
            const h24 = nowSlot.getHours();
            const mm = nowSlot.getMinutes();
            const am = h24 >= 12 ? "PM" : "AM";
            let h12 = h24 % 12; if (h12 === 0) h12 = 12;
            setHourStr(pad2(h12));
            setMinuteStr(pad2(mm));
            setAmpm(am);
        } else {
            const h24Sel = to24h(hourStr, ampm);
            const [y, m, d] = dateStr.split("-").map(Number);
            const selected = new Date(y, m - 1, d, h24Sel, parseInt(minuteStr || "0", 10), 0, 0);
            if (selected < nowSlot) {
                const h24 = nowSlot.getHours();
                const mm = nowSlot.getMinutes();
                const am = h24 >= 12 ? "PM" : "AM";
                let h12 = h24 % 12; if (h12 === 0) h12 = 12;
                setHourStr(pad2(h12));
                setMinuteStr(pad2(mm));
                setAmpm(am);
            }
        }
    }, [dateStr]); // re-check when date changes

    const isHourDisabled = (h12Str, ampmVal) => {
        if (!isTodaySelected) return false;
        const h24Opt = to24h(h12Str, ampmVal);
        return h24Opt < minTodaySlot.getHours();
    };

    const isMinuteDisabled = (mStr) => {
        if (!isTodaySelected) return false;
        if (!hourStr) return false;
        const h24Opt = to24h(hourStr, ampm);
        const minH = minTodaySlot.getHours();
        const minM = minTodaySlot.getMinutes();
        if (h24Opt < minH) return true;
        if (h24Opt > minH) return false;
        return parseInt(mStr, 10) < minM;
    };


    if (!isOpen) return null;

    const isSaving = patchLoading || resLoading;

    const groupDesc =
        scenarioGroups?.find(g => g.title === selectedGroup)?.description || "";


    /** ---------- render ---------- */
    return (
        <>
            {isSaving && <LoadingScreen />}
            <div
                className="yads-edit-overlay"
                onClick={(e) => {
                    if (e.target.classList.contains("yads-edit-overlay")) onClose?.();
                }}
            >
                <div className="yads-edit-modal wide" role="dialog" aria-modal="true">
                    <div className="yads-edit-header">
                        <h4 className="yads-edit-title">Edit Scheduled Ingestion</h4>
                    </div>

                    <div className="yads-edit-body">


                        {/* three-pane UI (reuses your classes) */}
                        <div className="yads-container" style={{ paddingTop: 0, paddingBottom: 0 }}>
                            {/* Left: groups */}
                            <div className="panel scenario-groups">
                                <h4 className="section-title-yads">Scenario Groups</h4>
                                <div className="scenario-group-list">
                                    {(scenarioGroups || []).map((group) => (
                                        <div
                                            key={group.title}
                                            className={`group-item ${selectedGroup === group.title ? "selected" : ""}`}
                                            onClick={() => {
                                                if (selectedGroup !== group.title) restoreGroupScenarios(group);
                                            }}
                                        >
                                            <span>{group.title}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Middle: scenarios */}
                            <div className={`panel scenario-details ${!selectedGroup ? "blurred" : ""}`}>
                                {!selectedGroup && (
                                    <div className="blur-overlay">
                                        <p>Please select a Scenario Group to continue</p>
                                    </div>
                                )}

                                <div className="scenario-header">
                                    <h5 className="group-title">{selectedGroup || "—"}</h5>
                                </div>

                                <div
                                    className="group-description"
                                    dangerouslySetInnerHTML={{
                                        __html: groupDesc || "No description available.",
                                    }}
                                />

                                {selectedGroup && (
                                    <>
                                        <div style={{ width: "100%", display: "flex", justifyContent: "flex-start" }}>
                                            <div className="scenario-mode-tabs">
                                                <span
                                                    className={`tab ${getCurrentMode() === "Default" ? "active" : ""}`}
                                                    onClick={() => handleScenarioModeChange("Default")}
                                                >
                                                    Default
                                                </span>
                                                <span
                                                    className={`tab ${getCurrentMode() === "Advanced" ? "active" : ""}`}
                                                    onClick={() => handleScenarioModeChange("Advanced")}
                                                >
                                                    Advanced
                                                </span>
                                                <span
                                                    className={`tab ${getCurrentMode() === "Reset" ? "active" : ""}`}
                                                    onClick={() => handleScenarioModeChange("Reset")}
                                                >
                                                    Reset
                                                </span>
                                            </div>
                                        </div>
                                        <br />

                                        {getCurrentMode() === "Advanced" && selectedGroupScenarios.length > 0 && (
                                            <div className="scenario-item select-all-row">
                                                <input
                                                    ref={selectAllRef}
                                                    type="checkbox"
                                                    checked={getAdvancedSelectionState() === "all"}
                                                    onChange={handleSelectAllScenarios}
                                                />
                                                <div className="scenario-info">
                                                    <span className="scenario-title">Select all in {selectedGroup}</span>
                                                </div>
                                            </div>
                                        )}

                                        {getCurrentMode() !== "Default" &&
                                            selectedGroupScenarios.length > 0 &&
                                            selectedGroupScenarios.map((scenario, index) => {
                                                const isInit = isInitScenario(scenario);
                                                return (
                                                    <div className="scenario-item" key={scenario.id}>
                                                        <input
                                                            type="checkbox"
                                                            checked={scenario.selected}
                                                            onChange={() => handleScenarioToggle(index)}
                                                        />
                                                        <div className="scenario-info">
                                                            <span className="scenario-title">
                                                                {scenario.title}
                                                                {isInit && " (required)"}
                                                            </span>
                                                            {!!scenario.description && (
                                                                <p className="scenario-desc">{scenario.description}</p>
                                                            )}
                                                        </div>
                                                        <div className="scenario-metric">
                                                            <input
                                                                type="number"
                                                                className="scenario-count"
                                                                value={isDurationScenario(scenario) ? scenario.durationHours : scenario.count}
                                                                min="1"
                                                                onChange={(e) => updateScenarioCount(index, e.target.value)}
                                                                onBlur={(e) => sanitizeScenarioCount(index, e.target.value)}
                                                                onKeyDown={(e) => {
                                                                    if (["e", "E", "+", "-"].includes(e.key)) e.preventDefault();
                                                                }}
                                                                disabled={isInit}
                                                                title={
                                                                    isInit
                                                                        ? "Count locked for Init"
                                                                        : isDurationScenario(scenario)
                                                                            ? "Enter duration in hours"
                                                                            : "Enter number of runs"
                                                                }
                                                            />
                                                            <span className="scenario-unit-hint">
                                                                {isDurationScenario(scenario) ? "hours" : "runs"}
                                                            </span>
                                                        </div>
                                                    </div>
                                                );
                                            })}

                                        {getCurrentMode() === "Reset" && (
                                            <div className="rtic-reset-inline" style={{ marginTop: 6 }}>
                                                <label className="rtic-checkbox-row">
                                                    <input
                                                        id="rtic-reset-checkbox"
                                                        type="checkbox"
                                                        checked={!!resetRticCluster}  // null/false => unchecked
                                                        onChange={(e) => {
                                                            setResetRticCluster(e.target.checked); // true/false
                                                            setRticTouched(true);                 // send only if user touched
                                                        }}
                                                    />
                                                    <span className="rtic-checkbox-text">
                                                        RTIC Cluster Reset
                                                    </span>
                                                </label>

                                                {/* ✅ RTIC-specific warning */}

                                                <div className="rtic-warning">
                                                    <div className="rtic-warning__icon">⚠️</div>
                                                    <div className="rtic-warning__text">
                                                        Enabling this will reset the RTIC cluster across all services. Incidents will be cleared from the cluster but will not be resolved. The service-level reset will resolve incidents for this service.
                                                    </div>
                                                </div>

                                            </div>
                                        )}

                                        {getCurrentMode() === "Default" && selectedGroupScenarios.length > 0 && (
                                            <div className="default-scenario-list">
                                                {selectedGroupScenarios.map((scenario) => (
                                                    <div className="scenario-readonly" key={scenario.id}>
                                                        <div className="readonly-header">
                                                            <span className="scenario-title">{scenario.title}</span>
                                                            <span className="scenario-count-readonly">
                                                                {isDurationScenario(scenario)
                                                                    ? `${getScenarioValue(scenario)} hour${getScenarioValue(scenario) > 1 ? "s" : ""}`
                                                                    : `x${getScenarioValue(scenario)}`}
                                                            </span>
                                                        </div>
                                                        {!!scenario.description && (
                                                            <div className="scenario-desc">{scenario.description}</div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>

                            {/* Right: selected items */}
                            <div className={`panel ingestion-config ${selectedItems.length === 0 ? "blurred" : ""}`}>
                                {selectedItems.length === 0 && (
                                    <div className="blur-overlay">
                                        <p>Please select at least one Scenario or Group</p>
                                    </div>
                                )}

                                <div className="section-header">
                                    <h6>Selected Items</h6>
                                    <hr />
                                </div>

                                <DragDropContext onDragEnd={handleDragEnd}>
                                    <Droppable droppableId="selected-tags" direction="vertical">
                                        {(provided) => (
                                            <div className="selected-tags" {...provided.droppableProps} ref={provided.innerRef}>
                                                {selectedItems.map((item, index) => (
                                                    <Draggable key={item.id} draggableId={item.id} index={index}>
                                                        {(provided) => (
                                                            <span
                                                                className="tag ordered-tag"
                                                                ref={provided.innerRef}
                                                                {...provided.draggableProps}
                                                                {...provided.dragHandleProps}
                                                            >
                                                                <span className="order-index">{index + 1}.</span>
                                                                {item.label}
                                                                <button onClick={() => handleRemoveSelectedItem(item.id)}>×</button>
                                                            </span>
                                                        )}
                                                    </Draggable>
                                                ))}
                                                {provided.placeholder}
                                            </div>
                                        )}
                                    </Droppable>
                                </DragDropContext>


                                {/* Schedule (moved here) */}



                                <div className="section-header" style={{ marginTop: 8 }}>
                                    <h6>Tenant</h6>
                                    <hr />
                                </div>

                                <div className="new-tenant-form">
                                    <label htmlFor="tenant-url">Tenant URL</label>
                                    <input
                                        id="tenant-url"
                                        type="text"
                                        className="yads-edit-input"
                                        value={tenantForm.tenantUrl}
                                        onChange={(e) => setTenantForm(f => ({ ...f, tenantUrl: e.target.value }))}
                                        placeholder="https://{tenant}-itom-demo.onbmc.com"
                                    />

                                    <label htmlFor="itsm-url">ITSM URL</label>
                                    <input
                                        id="itsm-url"
                                        type="text"
                                        className="yads-edit-input"
                                        value={tenantForm.itsmUrl}
                                        onChange={(e) => setTenantForm(f => ({ ...f, itsmUrl: e.target.value }))}
                                        placeholder="https://{tenant}-demo-restapi.onbmc.com"
                                    />

                                    <div className="row">
                                        <div className="field">
                                            <label htmlFor="access-key">ITOM Access Key</label>
                                            <input
                                                id="access-key"
                                                type="text"
                                                value={tenantForm.key}
                                                onChange={(e) => setTenantForm(f => ({ ...f, key: e.target.value }))}
                                            />
                                        </div>

                                        <div className="field">
                                            <label htmlFor="secret-key">ITOM Secret Key</label>
                                            <input
                                                id="secret-key"
                                                type="password"
                                                value={tenantForm.secretKey}
                                                onChange={(e) => setTenantForm(f => ({ ...f, secretKey: e.target.value }))}
                                            />
                                        </div>
                                    </div>

                                    <div className="row">
                                        <div className="field">
                                            <label htmlFor="itsm-username">ITSM Username</label>
                                            <input
                                                id="itsm-username"
                                                type="text"
                                                value={tenantForm.username}
                                                onChange={(e) => setTenantForm(f => ({ ...f, username: e.target.value }))}
                                            />
                                        </div>

                                        <div className="field">
                                            <label htmlFor="itsm-password">ITSM Password</label>
                                            <input
                                                id="itsm-password"
                                                type="password"
                                                value={tenantForm.password}
                                                onChange={(e) => setTenantForm(f => ({ ...f, password: e.target.value }))}
                                            />
                                        </div>
                                    </div>
                                </div>



                                <div className="section-header" style={{ marginTop: 8 }}>
                                    <h6>Schedule</h6>
                                    <hr />
                                </div>

                                <div className="sched-modal__inputs">
                                    <label className="sched-input">
                                        <span className="sched-input__label">Select Date</span>
                                        <div className="sched-input__wrap">
                                            <input
                                                type="date"
                                                className="sched-input__control"
                                                value={dateStr}
                                                min={todayLocalDateStr}
                                                onChange={(e) => {
                                                    const v = e.target.value;
                                                    setDateStr(v && v < todayLocalDateStr ? todayLocalDateStr : v);
                                                }}
                                                required
                                            />
                                            <span className="sched-input__caret">▾</span>
                                        </div>
                                    </label>

                                    <label className="sched-input">
                                        <span className="sched-input__label">Select Time</span>
                                        <div className="time-group" role="group" aria-label="Select time">
                                            {/* Hour */}
                                            <div className="time-seg">
                                                <select
                                                    className="time-seg__select"
                                                    aria-label="Hour"
                                                    value={hourStr}
                                                    onChange={(e) => setHourStr(e.target.value)}
                                                    required
                                                >
                                                    <option value="">HH</option>
                                                    {HOURS_12.map((h) => (
                                                        <option key={h} value={h} disabled={isHourDisabled(h, ampm)}>
                                                            {h}
                                                        </option>
                                                    ))}
                                                </select>
                                                <span className="time-seg__caret">▾</span>
                                            </div>

                                            <span className="time-sep">:</span>

                                            {/* Minute */}
                                            <div className="time-seg">
                                                <select
                                                    className="time-seg__select"
                                                    aria-label="Minute"
                                                    value={minuteStr}
                                                    onChange={(e) => setMinuteStr(e.target.value)}
                                                    required
                                                >
                                                    <option value="">MM</option>
                                                    {MINUTES.map((m) => (
                                                        <option key={m} value={m} disabled={isMinuteDisabled(m)}>
                                                            {m}
                                                        </option>
                                                    ))}
                                                </select>
                                                <span className="time-seg__caret">▾</span>
                                            </div>

                                            {/* AM/PM */}
                                            <div className="time-seg time-seg--ampm">
                                                <select
                                                    className="time-seg__select"
                                                    aria-label="AM or PM"
                                                    value={ampm}
                                                    onChange={(e) => setAmpm(e.target.value)}
                                                    required
                                                >
                                                    <option value="AM">AM</option>
                                                    <option value="PM">PM</option>
                                                </select>
                                                <span className="time-seg__caret">▾</span>
                                            </div>
                                        </div>
                                    </label>


                                </div>


                            </div>
                        </div>
                    </div>

                    <div className="yads-edit-footer">
                        <button className="btn-ghost" onClick={onClose}>Cancel</button>
                        <button className="btn-primary" onClick={handleSave}>Update</button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Yads_Ingestion_Scheduled_Edit;
